import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import Form from 'components/ui/Form';
import { getUnits } from 'actions/landlordPropertiesActions';
import {
  isLegalNotice,
  getTemplate,
  getTemplateValue,
  isTemplate,
  isNoticeOfEntryNotice,
  modes,
} from '../NoticeHelpers';
import PropertyMultiselectAdvanced from 'components/shared/PropertyMultiselectAdvanced';
import MultiSelect from '../../../NoticeMultiselect';

const FullWidthSelect = styled(MultiSelect)`
  width: 100%;
`;

const PropertyInput = ({ values, errors, touched, setFieldValue, properties, noticeTemplates }) => {
  const templateDelivery = useMemo(() => {
    return getTemplateValue(
      getTemplate(noticeTemplates, values.notice_template_id),
      'delivery',
      'any'
    );
  }, [noticeTemplates, values]);

  const filteredProperties = useMemo(() => {
    return getTemplateValue(
      getTemplate(noticeTemplates, values.notice_template_id),
      'properties',
      []
    );
  }, [values.notice_template_id, noticeTemplates]);

  const shouldFilterProperties = useMemo(() => {
    return getTemplateValue(
      getTemplate(noticeTemplates, values.notice_template_id),
      'assign_to_properties',
      false
    );
  }, [noticeTemplates, values.notice_template_id]);

  const propertyOptions = useMemo(() => {
    let options = properties;

    // If this is a legal notice, only allow properties that have that flag set
    if (isLegalNotice(values)) {
      options = options.filter((p) => !!p.can_send_legal_notices);
    }

    // If we're not using a template, template isn't for specific properties or
    // we don't have a list of properties to allow selection from, return what we've got.
    if (!isTemplate(values) || !shouldFilterProperties || filteredProperties.length === 0) {
      return options;
    }

    return options.filter((property) => filteredProperties.includes(property.value));
  }, [values, filteredProperties, shouldFilterProperties, properties]);

  const handleChange = (properties) => {
    // Clearing the field means properties === undefined;
    let setProperties = properties || [];
    setProperties = Array.isArray(setProperties) ? setProperties : [setProperties];

    if (
      isLegalNotice(values) ||
      isNoticeOfEntryNotice(values) ||
      templateDelivery === 'single_unit'
    ) {
      setFieldValue('delivery', 'units_within_property');
      setFieldValue('mode', modes.SELECTING_UNIT);
      if (setProperties.length !== 0) {
        getUnits(setProperties[0], { for_multiselect_with_floor_riser: true });
      }
    } else {
      setFieldValue('delivery', 'directly_to_property');
      setFieldValue('mode', modes.SELECTING_PROPERTY_OPTIONS);
    }

    setFieldValue('unit_option', 'directly_to_unit');
    setFieldValue('units', []);
    setFieldValue('properties', setProperties);
  };

  const isMultiPropertySelect = () => {
    // Legal notices can only be sent to a unit within a single property
    if (isLegalNotice(values) || isNoticeOfEntryNotice(values)) {
      return false;
    }
    // Single property selection if the template provided is configured for a single
    // property and unit - multiselect otherwise
    return !['single_property', 'single_unit'].includes(templateDelivery);
  };

  const renderInputControl = () => {
    if (isMultiPropertySelect()) {
      return (
        <PropertyMultiselectAdvanced
          selectablePropertyIds={propertyOptions.map((p) => parseInt(p.value))}
          name="properties"
          onChange={handleChange}
          value={values.properties}
        />
      );
    }

    return (
      <FullWidthSelect
        options={propertyOptions}
        name="properties"
        fullWidth
        withResetSelectAllButtons
        multiple={false}
        value={values.properties}
        onChange={handleChange}
      />
    );
  };

  return (
    <Form.Item
      label={`${getTranslatedString('landlordPortal.noticeAssignmentForm.properties')} *`}
      validateStatus={touched.properties && errors.properties ? 'error' : null}
      help={touched.properties && errors.properties}
    >
      {renderInputControl()}
    </Form.Item>
  );
};

export default connect(
  (store) => {
    return {
      properties: store.landlordProperties.properties,
      noticeTemplates: store.landlordNotices.notice_templates,
    };
  },
  {
    getUnits,
  }
)(PropertyInput);
